import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";
import { ActionType } from "../../constants/enums/action-type-enum";
import { ModuleType } from "../../constants/enums/module-type-enum";
import { SMRTabEnum } from "../../constants/enums/smr-tab-enum";
import userTypeEnum from "../../constants/enums/user-type-enum";
import RoutesConstants from "../../constants/routes.constant";
import { useStore } from "../../contexts/store-provider";
import { isOperationPermittedRoleBase } from "../../helpers/action-permission-helper";
import {
  getHeadquarterIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../helpers/localstorage-helper";
import FormLoader from "../../shared-components/FormLoader/FormLoader";
import LanguageSlider from "../../shared-components/language-slider/language-slider";
import NodataIcon from "../../shared-components/no-data-svg/no-data-icon";
import AddPopupHOC from "../../shared-components/popup/add/add-popup-hoc";
import { formatMessage } from "../../translations/format-message";
import AddEditCustomerManagement from "../customer-management/components/add-edit-customer-management";
import AgreementRecord from "./components/agreement-record";
import BookingCard from "./components/booking-card";
import CustomerInfoCard from "./components/customer-info-card";
import MedicalRecord from "./components/medical-record";
import SalesRecord from "./components/sales-record";
import "./sales-medical-records.css";
import { useConfirmDialog } from "../../shared-components/popup/smr-confirmation/smr-confirmation";
import { useHistory } from "react-router-dom";

const SalesMedicalRecords = (props: any) => {
  const history = useHistory();
  
  const { smrStore, headquarterStore, customerManagementStore } = useStore();
  const { showConfirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  const cardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const medicalCardRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const shouldPreventScroll = useRef(false);
  const scrollFromAgreementHistoryToBookingCard = (id: string) => {
    const el = cardRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const {
    GetHeadquarterByIdService,
    headquarterIcons,
    resetGetHeadquarterDetail,
  } = headquarterStore;
  const { addUpdateCustomerState, GetCustomerByIdService } =
    customerManagementStore;
  const {
    GetBookingListByCustomerIdService,
    allBookingList,
    resetBookingListState,
    selectedBookingId,
    setSelectedBookingId,
    registerSale,
    setRegisterSale,
    deleteSMRState,
    resetDeleteSMRState,
    resetStore,
    activeTab,
    setActiveTab,
    resetMedicalRecordListState,
    selectedSaleRecrodForCreatingNewSale,
    setSelectedSaleRecrodForCreatingNewSale,
    setIsDataChanged,
    isDataChanged,
    resetSalesRecordListState
  } = smrStore;

  
  const getSMRInfoFromStorage = () => {
    try {
      const data = localStorage.getItem("lizera_smr_info");

      if (!data) {
        return null;
      }

      const parsed = JSON.parse(data);

      if (!parsed?.customerId) {
        return null;
      }

      return {
        customerId: parsed.customerId,
        storeId: parsed?.storeId ?? ""
      };
    } catch (error) {
      console.error("invalid_lizera_smr_info");
      return null;
    }
  };
  
  const smrInfo = getSMRInfoFromStorage();
  const customerId = smrInfo?.customerId ?? -1;
  const storeId = smrInfo?.storeId ?? "";

  useEffect(() => {
    if (!smrInfo) {
      history.replace(RoutesConstants.Dashboard);
    }
  }, [smrInfo, history]);

  // Update scrollToSelectedBooking helper function
  const scrollToSelectedBooking = () => {
    if (!selectedBookingId || selectedBookingId === -1) return;
    
    // Check if we should prevent scroll (e.g., during refund or new sale)
    if (shouldPreventScroll.current) {
      shouldPreventScroll.current = false;
      return;
    }
    
    const ref = cardRefs.current[selectedBookingId];
    if (ref) {
      setTimeout(() => {
        ref.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }, 100);
    }
  };

  // Update the handleTabSelect function to include scrolling
  const handleTabSelect = async (key: string | null) => {
  if (!key || key === activeTab) return;

  if (smrStore.isDataChanged) {
    const confirmed = await showConfirmDialog(
      "confirmation",
      "unsaved_changes_warning"
    );
    if (!confirmed) return;
    smrStore.setIsDataChanged(false);
  }

  if (key === SMRTabEnum.Medical && allBookingList.length > 0) {
    resetMedicalRecordListState();
  }

  if (key) setActiveTab(key);

  if (registerSale) {
    setRegisterSale(false);
    if (customerId > 0 && storeId > 0) {
      GetBookingListByCustomerIdService(customerId, storeId);
    }
  }

  // Scroll to selected booking after tab change
  setTimeout(() => {
    scrollToSelectedBooking();
  }, 200);
};

  // Update the useEffect for selectedBookingId
  useEffect(() => {
    scrollToSelectedBooking();
  }, [selectedBookingId]);

  useEffect(() => {
    if (allBookingList.length > 0 && selectedBookingId && selectedBookingId !== -1) {
      setTimeout(() => {
        scrollToSelectedBooking();
      }, 300);
    }
  }, [allBookingList.length]);

  useEffect(() => {
    return () => {
      resetStore();
      setActiveTab(SMRTabEnum.Sales);
      smrStore.setIsDataChanged(false);
    };
  }, []);

  useEffect(() => {
    if (customerId > 0) {
      let headQuarerId = getHeadquarterIdFromLocalStorage();
      if (headQuarerId > 0)
        GetHeadquarterByIdService(getHeadquarterIdFromLocalStorage());
      GetBookingListByCustomerIdService(customerId, storeId);
    }
    return () => {
      resetBookingListState();
      resetGetHeadquarterDetail();
    };
  }, [customerId]);

  useEffect(() => {
    if (deleteSMRState.success) {
      toast.success(formatMessage("deleted_success"));
      GetBookingListByCustomerIdService(customerId, storeId);
      let userTypeFromLocalStorage = getUserTypeFromLocalStorage();
      GetCustomerByIdService(
        customerId,
        userTypeFromLocalStorage !== userTypeEnum[userTypeEnum.SuperAdmin] &&
          userTypeFromLocalStorage !== userTypeEnum[userTypeEnum.HeadQuarter]
          ? storeId
          : null
      );
      resetDeleteSMRState();
    }
  }, [deleteSMRState.success]);

  useEffect(() => {
    if (!selectedBookingId) return;

    const ref = cardRefs.current[selectedBookingId];
    if (ref) {
      ref.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  }, [selectedBookingId]);

  useEffect(() => {
    const unlisten = history.listen((nextLocation) => {
      // If navigating away from SMR route
      if (!nextLocation.pathname.includes(RoutesConstants.SalesMedicalRecords)) {
        localStorage.removeItem("lizera_smr_info");
      }
    });

    return () => {
      unlisten();
    };
  }, [history]);

  if (!smrInfo) return null;

  return (
    <>
      <ConfirmDialogComponent />
      {props.showAddPopup && (
        <AddEditCustomerManagement
          id={customerId}
          viewOnly={false}
          routeFrom={RoutesConstants.SalesMedicalRecords}
          modalClosed={props.addToggleHandler}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="sales_medical_record" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="reservation_management" />
                </li>
                <li className="breadcrumb-item" aria-current="page">
                  <FormattedMessage id="sales_medical_record" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      {addUpdateCustomerState.inProgress && (
        <FormLoader loading={addUpdateCustomerState.inProgress} />
      )}
      <div className="container-fluid mt-4 p-0">
        <div className="card booking-wrapper">
          <div className="row g-0">
            <div className="col-12 col-xl-2 booking-sidebar">
              <div className="row g-0">
                <div className="col-xl-12 col-4">
                <CustomerInfoCard
                  storeId={storeId}
                  selectedCustomerId={customerId}
                  openCustomerDialogHandler={async()=>{
                    if (smrStore.isDataChanged) {
                      const confirmed = await showConfirmDialog(
                        "confirmation",
                        "unsaved_changes_warning"
                      );
                      if (!confirmed) return;
                      smrStore.setIsDataChanged(false); // reset if confirmed
                    }
                    props.addToggleHandler();
                  }}   
                />
                </div>
                <div className="col-8 col-xl-12 position-relative">
                  <div className="p-2">
                    {activeTab === SMRTabEnum.Sales &&
                      isOperationPermittedRoleBase(
                        ModuleType.SalesMedicalRecord,
                        ActionType.Add
                      ) && (
                        <button
                          className="btn btn-primary w-100 mt-3 register_sale_btn"
                          onClick={async () => {
                            if (smrStore.isDataChanged) {
                              const confirmed = await showConfirmDialog(
                                "confirmation",
                                "unsaved_changes_warning"
                              );
                              if (!confirmed) return;
                              smrStore.setIsDataChanged(false); // reset if confirmed
                            }
                            resetSalesRecordListState();
                            setSelectedBookingId(-1);
                            setRegisterSale(true);
                            smrStore.setIsRegisterButtonClicked(true);
                          }}
                        >
                          <i className="bi bi-plus-lg"></i>
                          {formatMessage("register_sales")}
                        </button>
                      )}
                    {/* booking list */}
                    <div className="booking-list">
                      {allBookingList.length > 0 ? (
                        allBookingList.map((item, index) => {
                          return (
                            <BookingCard
                              index={index}
                              item={item}
                              key={item.Id}
                              ref={(el: any) => {
                                cardRefs.current[item.Id] = el;
                              }}
                              isActive={item.Id === selectedBookingId}
                              onClick={async () => {
                                if (item.Id === selectedBookingId) return;

                                if (smrStore.isDataChanged) {
                                  const confirmed = await showConfirmDialog(
                                    "confirmation",
                                    "unsaved_changes_warning"
                                  );
                                  if (!confirmed) return;
                                  smrStore.setIsDataChanged(false); // reset if confirmed
                                }
                                setRegisterSale(false);
                                if (selectedSaleRecrodForCreatingNewSale > 0) {
                                  setSelectedSaleRecrodForCreatingNewSale(-1);
                                }
                                  setSelectedBookingId(item.Id);
                                //  if (activeTab === SMRTabEnum.Medical)
                                  // setTimeout(() => {
                                  //   const el = cardRefs.current[item.Id];
                                  //   if (el) {
                                  //     el.scrollIntoView({
                                  //       behavior: "smooth",
                                  //       block: "start",
                                  //     });
                                  //   }
                                  // }, 300);
                              }}
                              icons={headquarterIcons}
                            />
                          );
                        })
                      ) : (
                        <div className="text-danger text-center no-data">
                          <NodataIcon />
                          <p className="text-danger">
                            <strong>
                              <FormattedMessage id="no_data_available" />
                            </strong>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-xl-10 main-content">
              <Tabs
                activeKey={activeTab}
                onSelect={handleTabSelect}
                id="booking"
                className="mb-3"
              >
                {/* Sales Tab */}
                <Tab eventKey={SMRTabEnum.Sales} title={formatMessage("sales")}>
                  <SalesRecord
                    customerId={customerId}
                    storeId={storeId}
                    shouldPreventScroll={shouldPreventScroll}
                    viewOnly={
                      !isOperationPermittedRoleBase(
                        ModuleType.SalesMedicalRecord,
                        ActionType.Add
                      )
                    }
                  />
                </Tab>

                {/* Medical Records Tab */}
                <Tab
                  eventKey={SMRTabEnum.Medical}
                  title={formatMessage("medical_records")}
                >
                  <MedicalRecord
                    customerId={customerId}
                    storeId={storeId}
                    viewOnly={
                      !isOperationPermittedRoleBase(
                        ModuleType.SalesMedicalRecord,
                        ActionType.Add
                      )
                    }
                    cardRef={medicalCardRef}
                  />
                </Tab>

                {/* Agreements Tab */}
                <Tab
                  eventKey={SMRTabEnum.Agreement}
                  title={formatMessage("agreements")}
                >
                  <AgreementRecord
                    customerId={customerId}
                    storeId={storeId}
                    scrollToCard={scrollFromAgreementHistoryToBookingCard}
                  />
                </Tab>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddPopupHOC(observer(SalesMedicalRecords));
